import { forwardRef } from "react";

import { useTheme } from "@mui/material";
import ButtonBase, { ButtonBaseProps } from "@mui/material/ButtonBase";

import { IconElement } from "../../../icons/types";
import { FormIcon } from "../FormIcon/FormIcon";

import { mergeSx } from "@/common/helpers/mergeSx";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";

type FormTextButtonProps = Omit<ButtonBaseProps, "variant" | "startAdornment" | "endAdornment" | "disableRipple"> & {
  startIcon?: IconElement;
  endIcon?: IconElement;
};

export const FormTextButton = forwardRef<HTMLButtonElement, FormTextButtonProps>(
  ({ children, startIcon, endIcon, sx, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms || !theme.palette.formsComponents?.textButton || !theme.palette.formsComponents?.global) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = mergeSx(
      {
        margin: -0.5,
        padding: 0.5,
        ...theme.typography.body2,
        color: theme.palette.formsComponents.textButton.textColor,
        borderRadius: theme.radius(1),
        outlineColor: "transparent",
        transition: "color 200ms ease-out, outline 100ms ease-out",
        ["&:hover"]: {
          color: theme.palette.formsComponents.textButton.hoverTextColor,
        },
        ["&:focus-visible"]: {
          color: theme.palette.formsComponents.textButton.textColor,
          outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
        },
        ["&.Mui-disabled"]: {
          color: theme.palette.formsComponents.textButton.textColor,
          opacity: 0.4,
        },
      },
      sx
    );

    return (
      <ButtonBase {...rest} ref={ref} sx={styles} disableRipple>
        {startIcon && (
          <FormIcon size="small" sx={mergeSx({ marginInlineStart: 0.5, marginInlineEnd: 0.5 })}>
            {startIcon}
          </FormIcon>
        )}
        {children}
        {endIcon && (
          <FormIcon size="small" sx={mergeSx({ marginInlineStart: 0.5, marginInlineEnd: 0.5 })}>
            {endIcon}
          </FormIcon>
        )}
      </ButtonBase>
    );
  }
);

FormTextButton.displayName = "FormTextButton";
